import QRCode from 'qrcode';
import { cfpBaseUrl } from '../network/urls';

const QR_GENERATE_PATH =
    process.env.REACT_APP_QR_GENERATE_PATH || 'api/L3FyL2ZvckRO';

const QR_RENDER_WIDTH_PX = 280;
const QR_RENDER_MARGIN = 2;
const QR_PRINT_SIZE_MM = 24;
const QR_GAP_MM = 10;
const QR_RIGHT_MARGIN_MM = 12;

/** New Connection — original DN placement (do not change for COA tuning). */
const NC_QR_LAYOUT = {
    imageY: 186,
    securityOffsetX: -26,
    securityOffsetY: 8,
    capitalOffsetY: 8,
};

/** Change of Attributes — signature-row placement; QR amount = capitalCostAmount only. */
export const COA_DN_QR_LAYOUT = {
    imageY: 158,
    securityOffsetX: -10,
    securityOffsetY: 4,
    capitalOffsetY: 4,
    // COA DN prints capitalCostAmount alone (not NC "Service Connection" sum).
    capitalCostOnly: true,
};

/**
 * RCO DN only — independent of NC / COA coordinates.
 * Drawn in reserved space under "Grid Share / Feeder Rehab" (call site sets imageY).
 */
export const RCO_DN_QR_LAYOUT = {
    imageY: 205,
    securityOffsetX: 0,
    securityOffsetY: 0,
    capitalOffsetY: 0,
    capitalCostOnly: true,
};

/** MCO DN — separate from NC/COA/RCO; amount = capitalCostAmount only. */
export const RCO_MCO_DN_QR_LAYOUT = {
    imageY: 186,
    securityOffsetX: -26,
    securityOffsetY: 8,
    capitalOffsetY: 8,
    capitalCostOnly: true,
};

const DN_QR_ENABLED_DISCO_CODES = new Set(['13', '14', '15']);

export const isDnQrEnabledForDisco = (discoCode) =>
    DN_QR_ENABLED_DISCO_CODES.has(
        String(discoCode || '').padStart(2, '0').slice(-2),
    );

const resolveDiscoCode = (discoCode, dnDets) => {
    if (discoCode != null && String(discoCode).trim() !== '') {
        return String(discoCode).padStart(2, '0').slice(-2);
    }

    const accountNo = String(dnDets?.connectionAccountNo || '');
    if (accountNo.length >= 4) {
        return accountNo.slice(2, 4);
    }

    return '';
};

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const MONTH_INDEX = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

export const formatDnQrDate = (dateString) => {
    if (!dateString) return '';
    const raw = String(dateString).trim();
    if (!raw) return '';

    const direct = raw.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
    if (direct) {
        const [, day, month, year] = direct;
        const yr = year.length === 2 ? year : year.slice(-2);
        return `${day.padStart(2, '0')}-${month.toUpperCase()}-${yr}`;
    }

    const date = new Date(raw);
    if (!Number.isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = MONTHS[date.getMonth()];
        const year = String(date.getFullYear()).slice(-2);
        return `${day}-${month}-${year}`;
    }

    const parts = raw.split('-');
    if (parts.length === 3) {
        const monthIdx = MONTH_INDEX[parts[1].toLowerCase().slice(0, 3)];
        if (monthIdx !== undefined) {
            const yearNum = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            const parsed = new Date(Number(yearNum), monthIdx, Number(parts[0]));
            if (!Number.isNaN(parsed.getTime())) {
                const day = String(parsed.getDate()).padStart(2, '0');
                return `${day}-${MONTHS[parsed.getMonth()]}-${String(parsed.getFullYear()).slice(-2)}`;
            }
        }
    }

    return raw.toUpperCase();
};

export const getDnAllotReferenceNo = (dnDets = {}) =>
    String(
        dnDets.connectionAccountNo ??
        dnDets.consumerAccountNo ??
        dnDets.refNo ??
        '',
    ).trim();

const parseDnChargeAmount = (value) => {
    if (value == null || value === '') return 0;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
};

/** Matches PDF "Service Connection" total (capital cost + ancillary DN charges). */
export const getTotalServiceConnectionAmount = (dnDets = {}) =>
    parseDnChargeAmount(dnDets.capitalCostAmount) +
    parseDnChargeAmount(dnDets.amountOfGridSharing) +
    parseDnChargeAmount(dnDets.amountOfFeederRehab) +
    parseDnChargeAmount(dnDets.civilWorkCharges) +
    parseDnChargeAmount(dnDets.fixedChargesOfMetering) +
    parseDnChargeAmount(dnDets.miscellaneousCharges) +
    parseDnChargeAmount(dnDets.mtCharges) +
    parseDnChargeAmount(dnDets.labourCharges) +
    parseDnChargeAmount(dnDets.designVetteingFee) +
    parseDnChargeAmount(dnDets.consultancyCharges) +
    parseDnChargeAmount(dnDets.variationsCharges) +
    parseDnChargeAmount(dnDets.dismentalmentCharges) +
    parseDnChargeAmount(dnDets.earthingCharges);

const unwrapQuotedString = (value) => {
    const cleaned = String(value ?? '').trim();
    if (
        (cleaned.startsWith('"') && cleaned.endsWith('"')) ||
        (cleaned.startsWith("'") && cleaned.endsWith("'"))
    ) {
        return cleaned.slice(1, -1).trim();
    }
    return cleaned;
};

const isApiErrorResponse = (json) => {
    const message = String(json?.Message || json?.message || '').toLowerCase();
    if (message.includes('authorization') && message.includes('denied')) return true;
    if (message.includes('unauthorized') || message.includes('invalid token')) return true;

    const status = json?.status ?? json?.Status ?? json?.statusCode ?? json?.StatusCode;
    if (status == null) return false;

    const normalized = String(status).toLowerCase();
    return !['200', '201', 'success', 'true', 'ok'].includes(normalized);
};

const findQrPayloadInJson = (value, depth = 0) => {
    if (depth > 4 || value == null) return null;

    if (typeof value === 'string') {
        const cleaned = unwrapQuotedString(value);
        if (!cleaned) return null;
        if (cleaned.startsWith('data:image')) return cleaned;
        if (/^00020[12]/.test(cleaned)) return cleaned;
        if (isBase64Image(cleaned)) return cleaned;
        if (cleaned.length > 40 && !cleaned.startsWith('{') && !cleaned.startsWith('[')) {
            return cleaned;
        }
        return null;
    }

    if (Array.isArray(value)) {
        for (const item of value) {
            const found = findQrPayloadInJson(item, depth + 1);
            if (found) return found;
        }
        return null;
    }

    if (typeof value === 'object') {
        const preferredKeys = [
            'qrString', 'QRString', 'qrCode', 'QRCode', 'qrData', 'QRData',
            'raastQr', 'RAASTQR', 'qrImage', 'image', 'data', 'Data', 'result', 'Result',
        ];
        for (const key of preferredKeys) {
            if (key in value) {
                const found = findQrPayloadInJson(value[key], depth + 1);
                if (found) return found;
            }
        }
        for (const nested of Object.values(value)) {
            const found = findQrPayloadInJson(nested, depth + 1);
            if (found) return found;
        }
    }

    return null;
};

const parseQrApiResponse = (text) => {
    if (!text) return null;
    const trimmed = unwrapQuotedString(text);
    if (!trimmed) return null;

    if (trimmed.startsWith('data:image')) return trimmed;
    if (/^00020[12]/.test(trimmed)) return trimmed;
    if (isBase64Image(trimmed)) return trimmed;

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            const json = JSON.parse(trimmed);
            if (isApiErrorResponse(json)) return null;
            return findQrPayloadInJson(json);
        } catch {
            return null;
        }
    }

    if (trimmed.length > 20) return trimmed;
    return null;
};

const isBase64Image = (value) => {
    const body = String(value || '')
        .trim()
        .replace(/^data:image\/[a-zA-Z0-9+.-]+;base64,/, '')
        .replace(/\s/g, '');

    if (!body || body.length < 80) return false;
    if (!/^[A-Za-z0-9+/=]+$/.test(body)) return false;

    return (
        body.startsWith('iVBORw0KGgo') ||
        body.startsWith('/9j/') ||
        body.startsWith('R0lGOD')
    );
};

const toImageDataUrl = (value) => {
    const cleaned = String(value || '').trim();
    if (!cleaned) return null;
    if (cleaned.startsWith('data:image')) return cleaned;

    const body = cleaned.replace(/^data:image\/[a-zA-Z0-9+.-]+;base64,/, '').replace(/\s/g, '');
    if (!isBase64Image(body)) return null;

    const mime = body.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
    return `data:${mime};base64,${body}`;
};

const toQrDataUrl = async (payload) => {
    if (!payload) return null;
    const cleaned = unwrapQuotedString(payload);
    if (!cleaned) return null;

    const imageDataUrl = toImageDataUrl(cleaned);
    if (imageDataUrl) return imageDataUrl;

    try {
        return await QRCode.toDataURL(cleaned, {
            errorCorrectionLevel: 'M',
            margin: QR_RENDER_MARGIN,
            width: QR_RENDER_WIDTH_PX,
        });
    } catch (error) {
        return null;
    }
};

const readQrApiResponse = async (response) => {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();

    if (contentType.includes('image/')) {
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result || null);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    }

    const text = await response.text();
    const parsed = parseQrApiResponse(text);
    return parsed ? toQrDataUrl(parsed) : null;
};

const fetchDnPaymentQrFromApi = async ({
    token,
    billNo,
    appNo,
    refNo,
    amount,
    dueDate,
    bPeriod,
    pType,
}) => {
    if (!token || !billNo) return null;

    const myHeaders = new Headers();
    myHeaders.append('Authorization', token);
    myHeaders.append('Content-Type', 'application/json');

    const raw = JSON.stringify({
        BillNo: String(billNo).trim(),
        AppNo: String(appNo || '').trim(),
        RefNo: String(refNo || '').trim(),
        Amount: Number(amount) || 0,
        DueDate: dueDate,
        BPeriod: bPeriod || dueDate,
        PType: String(pType),
    });

    try {
        const response = await fetch(`${cfpBaseUrl}${QR_GENERATE_PATH}`, {
            method: 'POST',
            headers: myHeaders,
            body: raw,
            redirect: 'follow',
        });

        if (!response.ok) {
        }

        return await readQrApiResponse(response);
    } catch (error) {
        return null;
    }
};

const getPdfImageFormat = (dataUrl) =>
    String(dataUrl || '').startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';

const resolveDueDate = (dnDets) =>
    formatDnQrDate(dnDets?.dueDate) ||
    formatDnQrDate(dnDets?.issueDate) ||
    formatDnQrDate(new Date());

const resolveAppNo = (applicationId) =>
    String(applicationId || '').trim();

const pickQrField = (obj, ...keys) => {
    for (const key of keys) {
        const value = obj?.[key];
        if (value != null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }
    return '';
};

const parseDnQrAmount = (value) => {
    if (value == null || value === '') return null;
    const parsed = Number(String(value).replace(/,/g, '').trim());
    return Number.isFinite(parsed) ? parsed : null;
};

/** getDemandNote returns capitalCostQR / securityAmountQR as bill id, JSON config, or legacy QR string. */
const parseDnQrSource = (source) => {
    if (source == null) return null;

    let value = source;
    if (typeof value === 'string') {
        const trimmed = unwrapQuotedString(value);
        if (!trimmed) return null;

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                value = JSON.parse(trimmed);
            } catch {
                return { type: 'qrString', value: trimmed };
            }
        } else if (
            /^00020[12]/.test(trimmed) ||
            isBase64Image(trimmed) ||
            trimmed.startsWith('data:image')
        ) {
            return { type: 'qrString', value: trimmed };
        } else {
            return {
                type: 'identifier',
                billNo: trimmed,
                appNo: trimmed,
                refNo: trimmed,
            };
        }
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        const billNo = pickQrField(value, 'BillNo', 'billNo');
        if (billNo) {
            const appNo = pickQrField(value, 'AppNo', 'appNo') || billNo;
            return {
                type: 'api',
                billNo,
                appNo,
                refNo: pickQrField(value, 'RefNo', 'refNo'),
                amount: parseDnQrAmount(value.Amount ?? value.amount),
                dueDate: pickQrField(value, 'DueDate', 'dueDate'),
                bPeriod: pickQrField(value, 'BPeriod', 'bPeriod', 'DueDate', 'dueDate'),
                pType: pickQrField(value, 'PType', 'pType'),
            };
        }
    }

    return null;
};

const resolveQrApiRequest = ({
    qrSource,
    allotRef,
    appNo,
    connectionAccountNo,
    amount,
    dueDate,
    pType,
}) => {
    const parsed = parseDnQrSource(qrSource);
    const resolvedDueDate = dueDate || '';

    if (parsed?.type === 'qrString') {
        return { parsed, request: null };
    }

    if (parsed?.type === 'identifier' || parsed?.type === 'api') {
        return {
            parsed,
            request: {
                billNo: parsed.billNo,
                appNo: parsed.appNo || parsed.billNo,
                refNo: connectionAccountNo || '',
                amount: parsed.amount ?? amount,
                dueDate: formatDnQrDate(parsed.dueDate) || resolvedDueDate,
                bPeriod:
                    formatDnQrDate(parsed.bPeriod) ||
                    formatDnQrDate(parsed.dueDate) ||
                    resolvedDueDate,
                pType: parsed.pType || pType,
            },
        };
    }

    return {
        parsed,
        request: {
            billNo: allotRef,
            appNo,
            refNo: connectionAccountNo || '',
            amount,
            dueDate: resolvedDueDate,
            bPeriod: resolvedDueDate,
            pType,
        },
    };
};

export const addDnPaymentQrCodesToPdf = async (
    doc,
    { dnDets, applicationId, token, securityAmount, capitalCost, discoCode, layout },
) => {
    const resolvedDiscoCode = resolveDiscoCode(discoCode, dnDets);
    if (!isDnQrEnabledForDisco(resolvedDiscoCode)) {
        return false;
    }

    const skipSecurityQr = Boolean(layout?.skipSecurity);
    const qrLayout = {
        ...(layout?.skipNcDefaults ? {} : NC_QR_LAYOUT),
        ...(layout || {}),
    };
    delete qrLayout.skipNcDefaults;
    delete qrLayout.skipSecurity;

    const allotRef = getDnAllotReferenceNo(dnDets);
    const connectionAccountNo = String(dnDets?.connectionAccountNo || '').trim();
    const dueDate = resolveDueDate(dnDets);
    const appNo = resolveAppNo(applicationId);

    // Capital Cost QR Amount must match what is printed on the DN:
    // - COA: only capitalCostAmount (e.g. 220) — never sum ancillary charges
    // - NC: Service Connection total (capital + ancillary)
    const serviceConnectionAmount = qrLayout.capitalCostOnly
        ? parseDnChargeAmount(
              dnDets?.capitalCostAmount != null && dnDets.capitalCostAmount !== ''
                  ? dnDets.capitalCostAmount
                  : capitalCost,
          )
        : getTotalServiceConnectionAmount(dnDets) ||
          parseDnChargeAmount(capitalCost) ||
          0;

    const resolvedSecurityAmount = skipSecurityQr
        ? 0
        : parseDnChargeAmount(
              securityAmount != null && securityAmount !== ''
                  ? securityAmount
                  : dnDets?.securityDepositeAmount,
          );

    const tasks = [];
    if (resolvedSecurityAmount > 0) {
        tasks.push({
            pType: '3',
            amount: resolvedSecurityAmount,
            label: 'Security',
            qrSource: dnDets?.securityAmountQR,
        });
    }
    if (serviceConnectionAmount > 0) {
        tasks.push({
            pType: '2',
            amount: serviceConnectionAmount,
            label: 'Capital Cost',
            qrSource: dnDets?.capitalCostQR,
        });
    }
    if (!tasks.length) return;

    const qrImages = await Promise.all(
        tasks.map(async (task) => {
            let dataUrl = null;
            const { parsed, request } = resolveQrApiRequest({
                qrSource: task.qrSource,
                allotRef,
                appNo,
                connectionAccountNo,
                amount: task.amount,
                dueDate,
                pType: task.pType,
            });

            if (request?.billNo) {
                dataUrl = await fetchDnPaymentQrFromApi({
                    token,
                    ...request,
                });
            }

            if (!dataUrl && parsed?.type === 'qrString') {
                dataUrl = await toQrDataUrl(parsed.value);
            }

            return { ...task, dataUrl };
        }),
    );

    const valid = qrImages.filter((item) => item.dataUrl);

    if (!valid.length) {
        const legacyItems = [];
        if (resolvedSecurityAmount > 0 && dnDets?.securityAmountQR) {
            const securityParsed = parseDnQrSource(dnDets.securityAmountQR);
            if (securityParsed?.type === 'qrString') {
                legacyItems.push({
                    label: 'Security',
                    dataUrl: await toQrDataUrl(securityParsed.value),
                });
            }
        }
        if (serviceConnectionAmount > 0 && dnDets?.capitalCostQR) {
            const capitalParsed = parseDnQrSource(dnDets.capitalCostQR);
            if (capitalParsed?.type === 'qrString') {
                legacyItems.push({
                    label: 'Capital Cost',
                    dataUrl: await toQrDataUrl(capitalParsed.value),
                });
            }
        }
        valid.push(...legacyItems.filter((item) => item.dataUrl));
    }

    if (!valid.length) {
        return;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const totalQrWidth =
        valid.length * QR_PRINT_SIZE_MM + Math.max(valid.length - 1, 0) * QR_GAP_MM;
    const startX = pageWidth - QR_RIGHT_MARGIN_MM - totalQrWidth;

    valid.forEach((item, index) => {
        let x = startX + index * (QR_PRINT_SIZE_MM + QR_GAP_MM);
        let y = qrLayout.imageY;

        if (item.label === 'Security') {
            x += qrLayout.securityOffsetX;
            y += qrLayout.securityOffsetY;
        }

        if (item.label === 'Capital Cost') {
            y += qrLayout.capitalOffsetY;
        }

        doc.addImage(
            item.dataUrl,
            getPdfImageFormat(item.dataUrl),
            x,
            y,
            QR_PRINT_SIZE_MM,
            QR_PRINT_SIZE_MM,
        );
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'bold');
        doc.text(item.label, x + QR_PRINT_SIZE_MM / 2, y + QR_PRINT_SIZE_MM + 4, { align: 'center' });
    });

    doc.setFont('helvetica', 'normal');
    return valid.length > 0;
};